import { Suspense, useEffect, useMemo, useCallback, lazy } from 'react';
import styled from 'styled-components';
import { trackGameClick } from '@comeon/analytics';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, type Location } from 'react-router-dom';
import { useRouter } from '~/utils/navigation';
import { updateMainContentLoading } from '~/components/brand/ducks/index';
import { selectIsBotRequest, selectPreloadTournamentsCategory } from '~/components/brand/ducks/selectors';
import { LIST_NAME } from '~/components/casino-shared/constants';
import useExploreData from '~/components/casino-shared/explore/use-explore-data';
import GameInfo from '~/components/casino-shared/game-info/game-info';
import { EXPLORE_CASINO_SUCCESS } from '~/components/casino/ducks/types';
import { EXPLORE_LIVE_CASINO_SUCCESS } from '~/components/live-casino/ducks/types';
import useCasinoLocation from '~/hooks/use-casino-location';
import breakpoint from '~/utils/style-utils';
import type { TrackGameClickParamsFromCategory } from '../types';
import StandardCategory from './categories/standard-category';
import ExploreSkeleton from './explore-skeleton';

const PreloadedTournamentsCategory = lazy(() => import('./categories/preloaded-tournaments-category'));
const CategoryPage = lazy(() => import('./categories/category-page'));

const ExploreContainer = styled.div`
	padding: 12px 0;

	${breakpoint.lg`
		 padding: 25px 0;
	`};
`;

const Explore = () => {
	const dispatch = useDispatch();
	const router = useRouter();
	const location = useLocation() as Location<{ categoryId?: string }>;
	const { isLiveCasinoLocation } = useCasinoLocation();
	const { exploreCategories, exploreCasinoStatus, exploreLiveCasinoStatus } = useExploreData();
	const exploreDataLoaded = isLiveCasinoLocation
		? exploreLiveCasinoStatus === EXPLORE_LIVE_CASINO_SUCCESS
		: exploreCasinoStatus === EXPLORE_CASINO_SUCCESS;
	const isBotRequest = useSelector(selectIsBotRequest);
	const preloadTournamentsCategory = useSelector(selectPreloadTournamentsCategory);
	const activeCategory = location?.state?.categoryId;

	const showPreloadedTournamentsCategory = useMemo(
		() =>
			exploreDataLoaded &&
			isBotRequest &&
			preloadTournamentsCategory &&
			!exploreCategories?.find(category => category.type === 'tournaments'),
		[exploreCategories, exploreDataLoaded, isBotRequest, preloadTournamentsCategory]
	);

	const handleTrackGameClick = (category: string) => (trackingData: TrackGameClickParamsFromCategory) => {
		trackGameClick({
			...trackingData,
			listName: LIST_NAME.EXPLORE,
			isLiveCasinoLocation,
			category
		});
	};

	useEffect(() => {
		dispatch(updateMainContentLoading('loaded'));
	}, [dispatch]);

	const openGameInfoPage = useCallback(
		(id: string) => {
			// TODO (navigation) - use search params or redux for state
			router.push(location.pathname, {
				state: {
					...location.state,
					categoryId: id,
					gameCode: ''
				}
			});
		},
		[router, location]
	);

	return (
		<ExploreContainer data-at="games-container">
			<Suspense fallback={<div className="spinner" />}>
				{showPreloadedTournamentsCategory && <PreloadedTournamentsCategory />}
				{exploreDataLoaded ? (
					exploreCategories?.map(category => (
						<StandardCategory
							key={category.id}
							category={category}
							openGameInfoPage={openGameInfoPage}
							trackGameClick={handleTrackGameClick(category?.title ?? category?.type)}
							listName={LIST_NAME.EXPLORE}
						/>
					))
				) : (
					<ExploreSkeleton />
				)}
				<GameInfo />
				{activeCategory && <CategoryPage trackGameClick={handleTrackGameClick} />}
			</Suspense>
		</ExploreContainer>
	);
};

export default Explore;
